import React, { useState, useCallback, Dispatch, SetStateAction, useEffect } from 'react';
import { faTrashCan, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { View, TouchableOpacity, Modal, ScrollView } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import MyDataTable from '../../../components/MyDataTable';
import AgentsDropdown from './AgentsDropDown';
import { translate } from '../../../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';
import FormActions from '../../../components/FormActions';
import { dispatchService } from '../../../services/central-api/dispatch';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import FinishEarlyReasonDropdown from '../../../components/dispatch/FinishEarlyReasonDropdown';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { UnitValuesForm } from './UnitForm';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';

interface UnitFormAgentsProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<UnitValuesForm, any, any>;
    unit: Unit | undefined;
    setUnit: Dispatch<SetStateAction<Unit | undefined>>;
    actingBodyId: number;
    modifiedAgentAssignments: { [agentId: number]: number; };
    setModifiedAgentAssignments: Dispatch<SetStateAction<{ [agentId: number]: number; }>>;
    isFinished: boolean;
}

export default function UnitFormAgents({
    unit,
    setUnit,
    form,
    actingBodyId,
    modifiedAgentAssignments,
    setModifiedAgentAssignments,
    isFinished,
}: UnitFormAgentsProps) {
    const { styles, theme } = useStyles(stylesheet);

    const [, updateState] = useState({});
    const forceUpdate = useCallback(() => updateState({}), []);

    const agents = useWatch({
        control: form.control,
        name: 'agents'
    });

    const [selectableAgents, setSelectableAgents] = useState<{ label: string; value: number; }[]>([]);
    const [currentAgentAssignment, setCurrentAgentAssignment] = useState<number>(0);
    const [isAssignmentModalVisible, setIsAssignmentModalVisible] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState<number>(0);
    const [modalSaveDisabled, setModalSaveDisabled] = useState(true);
    const [selectableAssignments, setSelectableAssignments] = useState<{ label: string; value: number; }[]>([]);

    const [finishEarlyReasonId, setFinishEarlyReasonId] = useState<number>(0);
    const [deactivateAgentModalVisible, setDeactivateAgentModalVisible] = useState(false);
    const [deactivatedAgent, setDeactivatedAgent] = useState<SimplifiedUserForUnit | undefined>();

    useEffect(() => {
        setSelectedAssignment(0);
        setModalSaveDisabled(true);
    }, [isAssignmentModalVisible]);

    useEffect(() => {
        if (selectedAssignment) {
            setModalSaveDisabled(false);
        }
    }, [selectedAssignment]);

    useEffect(() => {
        if (actingBodyId) {
            getAssignments(actingBodyId);
        }
    }, [actingBodyId]);

    async function getAssignments(actingBodyId: number) {
        try {
            const assignments = await dispatchService.getSimplifiedAssignments(actingBodyId);
            setSelectableAssignments([
                ...assignments.map((assignment) => {
                    return {
                        value: assignment.id,
                        label: assignment.name
                    };
                })
            ]);
        } catch (err) {
            console.error(err);
        }
    }

    async function deactivateAgent(unitId: number, agentId: number, finishEarlyReasonId: number) {
        try {
            await dispatchService.deactivateAgent(unitId, agentId, finishEarlyReasonId);
            setUnit(prevUnit => ({
                ...prevUnit!,
                UnitUsers: prevUnit?.UnitUsers?.map(user =>
                    user.id === agentId
                        ? {
                            ...user,
                            unitId: null
                        }
                        : user
                ) ?? []
            }));
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setDeactivatedAgent(undefined);
            setFinishEarlyReasonId(0);
        }
    }

    const disableAgentIcon = () => {
        if (unit && unit.definitiveStart && form.getValues('agents').length == 1) {
            return true;
        }

        return false;
    };

    return <>
        <View style={{ rowGap: 5, flex: 1, minWidth: 250 }}>
            {!isFinished &&
                <AgentsDropdown
                    value={agents}
                    setValue={(values: number[]) => {
                        form.setValue('agents', values);
                    }}
                    actingBodyId={actingBodyId}
                    disabled={false}
                    zIndex={3}
                    selectableAgents={selectableAgents}
                    setSelectableAgents={setSelectableAgents}
                    forceUpdate={forceUpdate}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            }
            <MyDataTable
                columns={[
                    {
                        name: translate('name'),
                        cell: row => {
                            if (isFinished && unit?.UnitUsers) {
                                const agent = unit?.UnitUsers.find(agent => agent.id == row.id);
                                return <View style={{ width: '100%' }}>
                                    <MyAppText >{agent?.name}{agent?.role?.name ? ' - ' + agent.role.name : ''}{agent?.warName ? ' - ' + agent?.warName : ''}: {agent?.registry}</MyAppText>
                                </View>;
                            }
                            const agent = selectableAgents.find(agent => agent.value == row.id);
                            return <View style={{ width: '100%' }}>
                                <MyAppText >{agent?.label}</MyAppText>
                            </View>;
                        },
                        grow: 2,
                        wrap: true
                    },
                    {
                        grow: 2,
                        name: translate('assignment'),
                        cell: row => {
                            if (isFinished && unit?.UnitUsers) {
                                const agent = unit.UnitUsers.find(agent => agent.id == row.id);

                                const assignment = selectableAssignments.find((assignment) => assignment.value == agent?.UnitUser.assignmentId);

                                return <View style={{ width: '100%' }}>
                                    <MyAppText >{assignment?.label}</MyAppText>
                                </View>;
                            }

                            const defaultButton = <TouchableOpacity onPress={() => {
                                setCurrentAgentAssignment(row.id);
                                setIsAssignmentModalVisible(true);
                            }} style={{ width: '100%' }}>
                                <MyAppText style={{
                                    textDecorationLine: 'underline',
                                    color: theme.colors.labelColor,
                                    textDecorationColor: theme.colors.labelColor,
                                }}>{translate('selectAssignment')}</MyAppText>
                            </TouchableOpacity>;

                            // first of all check if there's already a modification on user assignment on the page
                            if (row.id in modifiedAgentAssignments) {
                                const assignment = selectableAssignments.find((assignment) => assignment.value == modifiedAgentAssignments[row.id]);

                                if (!assignment?.label) {
                                    return defaultButton;
                                }

                                return <TouchableOpacity onPress={() => {
                                    setCurrentAgentAssignment(row.id);
                                    setIsAssignmentModalVisible(true);
                                }}>
                                    <MyAppText style={{ textDecorationLine: 'underline' }}>{assignment.label}</MyAppText>
                                </TouchableOpacity>;
                            }

                            // then try to get the current database information
                            if (!unit?.UnitUsers) {
                                return defaultButton;
                            }
                            const unitUser = unit.UnitUsers.find((unitUser) => unitUser.id == row.id);

                            if (!unitUser) {
                                return defaultButton;
                            }

                            const assignment = selectableAssignments.find((assignment) => assignment.value == unitUser.UnitUser.assignmentId);

                            if (!assignment?.label) {
                                return defaultButton;
                            }

                            return <TouchableOpacity onPress={() => {
                                setCurrentAgentAssignment(row.id);
                                setIsAssignmentModalVisible(true);
                            }} style={{ width: '100%' }}>
                                <MyAppText style={{ textDecorationLine: 'underline' }}>{assignment.label}</MyAppText>
                            </TouchableOpacity>;
                        },
                    },
                    {
                        grow: 1,
                        name: <View style={{ flexDirection: 'row', columnGap: 5 }}>
                            <MyAppText>{translate('configuration')}</MyAppText>
                            <MyAppText style={styles.requiredText}>(*)</MyAppText>
                        </View>,
                        cell: row => (
                            <View style={{ flexDirection: 'column', gap: 8, paddingVertical: 12 }}>
                                {form.watch('hasPlate') && <FilterCheckBox
                                    label={translate('vehicle_metadata_driver')}
                                    onChange={(val) => {
                                        form.setValue('driverId', val ? row.id : 0, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    value={form.watch('driverId') === row.id}
                                    labelStyle={{ fontSize: 14 }}
                                />}
                                <FilterCheckBox
                                    label={translate('personInCharge')}
                                    onChange={(val) => {
                                        form.setValue('personInChargeId', val ? row.id : 0, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    value={form.watch('personInChargeId') === row.id}
                                    labelStyle={{ fontSize: 14 }}
                                />
                            </View>
                        ),
                        wrap: true,
                    },
                    !isFinished ? {
                        name: translate('action'),
                        button: true,
                        grow: 0,
                        cell: row => {
                            const agent = unit?.UnitUsers?.find(agent => agent.id == row.id);
                            if (unit !== undefined && agent?.unitId == unit.id) {
                                return <TouchableOpacity onPress={() => {
                                    setDeactivateAgentModalVisible(true);
                                    setDeactivatedAgent(agent);
                                }} >
                                    <FontAwesomeIcon fontSize={20} icon={faUserSlash} color={theme.colors.iconColor} />
                                </TouchableOpacity>;
                            }
                            return <TouchableOpacity
                                onPress={() => {
                                    const idx = agents.findIndex((el) => el == row.id);
                                    const agentsSelected = agents;
                                    agentsSelected.splice(idx, 1);
                                    form.setValue('agents', [...agentsSelected]);
                                }}
                                disabled={disableAgentIcon()}>
                                <FontAwesomeIcon fontSize={20} icon={faTrashCan} color={theme.colors.iconColor} />
                            </TouchableOpacity>;
                        }
                    } : {}
                ]}
                data={agents.map(agent => ({ id: agent }))}
            />
        </View>

        <Modal
            transparent={true}
            visible={isAssignmentModalVisible}
            animationType='fade'
            onRequestClose={() => setIsAssignmentModalVisible(false)}
        >
            <View style={styles.modalContainer}>
                <View style={[styles.formContainer, { maxHeight: 160 }]}>
                    <View style={{ rowGap: 5, flex: 1 }}>
                        <FilterDropdown
                            label={translate('assignments')}
                            items={selectableAssignments}
                            multiple={false}
                            setValue={setSelectedAssignment}
                            value={selectedAssignment}
                            disabled={false}
                            zIndex={2}
                        />
                    </View>
                    <FormActions
                        onSubmit={() => {
                            if (selectedAssignment) {
                                setModifiedAgentAssignments({ ...modifiedAgentAssignments, [currentAgentAssignment]: selectedAssignment });
                            }
                            setIsAssignmentModalVisible(false);
                        }}
                        onClose={() => setIsAssignmentModalVisible(false)}
                        disabled={modalSaveDisabled}
                        confirmText='select' />
                </View>
            </View>
        </Modal>

        <Modal
            transparent={true}
            visible={deactivateAgentModalVisible}
            animationType='fade'
            onRequestClose={() => setDeactivateAgentModalVisible(false)}
        >
            <View style={styles.modalContainer}>
                <View style={[styles.formContainer, { maxHeight: 320, gap: 15 }]}>
                    <ScrollView contentContainerStyle={{ flex: 1 }}>
                        {deactivatedAgent && <FinishEarlyReasonDropdown
                            value={finishEarlyReasonId}
                            setValue={setFinishEarlyReasonId}
                            unitId={Number(deactivatedAgent.unitId)}
                        />
                        }
                    </ScrollView>
                    <FormActions
                        viewStyle={{ justifyContent: 'flex-end' }}
                        onSubmit={() => {
                            if (deactivatedAgent && deactivatedAgent.unitId && finishEarlyReasonId) {
                                deactivateAgent(deactivatedAgent.unitId, deactivatedAgent.id, finishEarlyReasonId);
                            }
                            setDeactivateAgentModalVisible(false);
                        }}
                        onClose={() => {
                            setDeactivateAgentModalVisible(false);
                            setDeactivatedAgent(undefined);
                            setFinishEarlyReasonId(0);
                        }}
                        disabled={finishEarlyReasonId == 0} />
                </View>
            </View>
        </Modal>
    </>;
}

const stylesheet = createStyleSheet(theme => ({
    modalContainer: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
        height: '100%'
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    requiredText: {
        color: theme.colors.errors['300'],
        fontWeight: '600'
    }
}));
